import React from 'react';

type StagingForecastToggleContextType = {
  shouldShowStagingForecastToggles: boolean;
  toggleStagingForecastToggles: () => void;
};

const StagingForecastToggleContext = React.createContext<StagingForecastToggleContextType>({
  shouldShowStagingForecastToggles: false,
  toggleStagingForecastToggles: () => {},
});

export function StagingForecastToggleProvider({children}: {children: React.ReactNode}) {
  // This shows or hides staging toggles to allow internal users to see staging forecasts.
  // The toggles are hidden by default, to prevent internal-only features from being visible
  // during any external meetings.

  const [shouldShowStagingForecastToggles, setShouldShowStagingForecastToggles] =
    React.useState(false);

  const toggleStagingForecastToggles = React.useCallback(() => {
    setShouldShowStagingForecastToggles((prev) => !prev);
  }, []);

  // Add keyboard shortcut listener
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Modifier + Shift + L to toggle staging toggles
      if (
        (event.ctrlKey || event.altKey || event.metaKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === 'l'
      ) {
        event.preventDefault();
        toggleStagingForecastToggles();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [toggleStagingForecastToggles]);

  return (
    <StagingForecastToggleContext.Provider
      value={{
        shouldShowStagingForecastToggles: shouldShowStagingForecastToggles,
        toggleStagingForecastToggles: toggleStagingForecastToggles,
      }}
    >
      {children}
    </StagingForecastToggleContext.Provider>
  );
}

export function useStagingForecastToggle() {
  return React.useContext(StagingForecastToggleContext);
}
